import { WithStatePaginatedTable } from '@delmonicos/shared-design-system-react/src/components/with-state-paginated-table/with-state-paginated-table';
import { TransformedPaginatedData } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transformed-paginated-data.model';
import { LocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/ocpi-cpo-api/models/locations/location.model';
import {
  ColumnOrderState,
  OnChangeFn,
  SortingState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StateEnum } from '@model/enum/state.enum';

import { EmspBoLocationListActionsColumn } from './emsp-bo-location-list-actions.column';
import { EmspBoLocationListAddressCell } from './emsp-bo-location-list-address.cell';
import { EmspBoLocationListCityCell } from './emsp-bo-location-list-city.cell';
import { EmspBoLocationListCountryCell } from './emsp-bo-location-list-country.cell';
import { EmspBoLocationListLastUpdatedCell } from './emsp-bo-location-list-last-updated.cell';
import { EmspBoLocationListNameCell } from './emsp-bo-location-list-name.cell';

const columnHelper = createColumnHelper<LocationModel>();

const COLUMN_ID_LIST = [
  'name',
  'address',
  'city',
  'country',
  'last_updated',
  'actions',
];

const COLUMN_ORDER: ColumnOrderState = COLUMN_ID_LIST;

const COLUMN_VISIBILITY: VisibilityState = COLUMN_ID_LIST.reduce(
  (visibility, columnId) => ({ ...visibility, [columnId]: true }),
  {},
);

interface EmspBoEmspLocationListViewProps {
  paginationLimit: number;
  paginationLimitList: number[];
  paginationState: StateEnum;
  paginationSorting: SortingState;
  resource?: TransformedPaginatedData<LocationModel>;
  onPaginationLimitChange: (paginationLimitNext: number) => void;
  onPaginationPageChange: (paginationPageNext: number) => void;
  onPaginationSortingChange?: OnChangeFn<SortingState>;
}

export const EmspBoLocationListView = ({
  paginationLimit,
  paginationLimitList,
  paginationState,
  paginationSorting,
  resource,
  onPaginationLimitChange,
  onPaginationPageChange,
  onPaginationSortingChange,
}: EmspBoEmspLocationListViewProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  // Nothing to do

  // LIST //////////////////////////////////////////////////////////////////////

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.name'),
        cell: EmspBoLocationListNameCell,
      }),
      columnHelper.accessor('address', {
        enableSorting: false,
        header: t(
          'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.address',
        ),
        cell: EmspBoLocationListAddressCell,
      }),
      columnHelper.accessor('city', {
        enableSorting: false,
        header: t('delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.city'),
        cell: EmspBoLocationListCityCell,
      }),
      columnHelper.accessor('country', {
        enableSorting: false,
        header: t(
          'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.country',
        ),
        cell: EmspBoLocationListCountryCell,
      }),
      columnHelper.accessor('last_updated', {
        enableSorting: false,
        header: t(
          'delmonicos-shared-sdk.outputs-ocpi-cpo-api-location.last-updated',
        ),
        cell: EmspBoLocationListLastUpdatedCell,
      }),
      columnHelper.display(EmspBoLocationListActionsColumn),
    ],
    [t],
  );

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <WithStatePaginatedTable
      paginatedData={resource}
      state={paginationState}
      sorting={paginationSorting}
      columns={columns}
      columnOrder={COLUMN_ORDER}
      columnVisibility={COLUMN_VISIBILITY}
      limit={paginationLimit}
      limitList={paginationLimitList}
      errorMessage={t('$.emsp-bo-location-list.error')}
      onLimitChange={onPaginationLimitChange}
      onPageChange={onPaginationPageChange}
      onSortingChange={onPaginationSortingChange}
    />
  );
};
